"use client"

import { useMemo } from 'react'

export interface TOCItem {
  id: string
  text: string
  level: number
  slug: string
}

interface UseTOCOptions {
  content: string
  showReadingTimes?: boolean
  minHeadings?: number
}

interface UseTOCReturn {
  tocItems: TOCItem[]
  shouldShowTOC: boolean
  sectionReadingTimes: Record<string, number>
  processedContent: string
}

// Simple slug generation
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

// Cache for heading extraction to avoid reprocessing
const headingCache = new Map<string, TOCItem[]>()

// Shared regex-based text extraction. Used on both server and client so the
// hook produces identical output during SSR and the first client render.
// (A previous implementation branched on typeof document — same algorithm
// here, just applied uniformly to avoid hydration drift.)
const HTML_TAG_REGEX = /<[^>]*>/g
const HTML_ENTITY_REGEX = /&(?:#x([0-9a-fA-F]+)|#(\d+)|nbsp|amp|lt|gt|quot|apos|#x27|#39);/g

function decodeHtmlEntities(text: string): string {
  return text.replace(HTML_ENTITY_REGEX, (entity, hex, dec) => {
    if (hex) return String.fromCharCode(parseInt(hex, 16))
    if (dec) return String.fromCharCode(parseInt(dec, 10))
    switch (entity) {
      case '&nbsp;': return ' '
      case '&amp;': return '&'
      case '&lt;': return '<'
      case '&gt;': return '>'
      case '&quot;': return '"'
      case '&apos;':
      case '&#x27;':
      case '&#39;': return "'"
      default: return entity
    }
  })
}

function extractHeadingText(headingInnerHtml: string): string {
  return decodeHtmlEntities(headingInnerHtml.replace(HTML_TAG_REGEX, ''))
    .replace(/\s+/g, ' ')
    .trim()
}

// Optimized heading extraction with caching
function extractHeadings(htmlContent: string): TOCItem[] {
  if (!htmlContent) return []
  
  // Check cache first
  const cached = headingCache.get(htmlContent)
  if (cached) {
    return cached
  }
  
  // More robust regex that handles:
  // 1. Attributes on heading tags (class, id, etc.)
  // 2. Nested HTML tags inside headings
  // 3. HTML entities
  // 4. Multiline content
  // Uses a balanced match approach for nested tags
  const headingRegex = /<(h[2-6])([^>]*)>([\s\S]*?)<\/\1>/gi
  const headings: TOCItem[] = []
  let globalIndex = 0
  
  // Use more efficient approach - process matches as we find them
  let match
  while ((match = headingRegex.exec(htmlContent)) !== null) {
    const level = parseInt(match[1].substring(1))
    const attributes = match[2] || ''
    const content = match[3] || ''

    // Extract existing id attribute if present
    const existingIdMatch = attributes.match(/id=["']([^"']+)["']/)
    const existingId = existingIdMatch ? existingIdMatch[1] : null

    const text = extractHeadingText(content)

    if (text.length > 1) {
      const slug = generateSlug(text)
      const id = existingId || `toc-${slug}-${globalIndex}`

      headings.push({
        id,
        text,
        level,
        slug
      })
      globalIndex++
    }
  }
  
  // Cache the result
  headingCache.set(htmlContent, headings)
  
  return headings
}

// Inject TOC ids into headings synchronously so SSR and the first client render
// produce identical HTML. Previously this lived in a useEffect that mutated
// state post-mount, which (a) caused a flash + extra render, (b) meant in-page
// hash navigation could target an id that didn't exist yet, and (c) was a
// hydration mismatch risk on iOS Safari. Pure function of (content, tocItems)
// — safe to memoize.
function injectHeadingIds(content: string, tocItems: TOCItem[]): string {
  if (!content || tocItems.length === 0) return content

  // Group headings by level to process them more efficiently
  const headingsByLevel = new Map<number, TOCItem[]>()
  tocItems.forEach(item => {
    if (!headingsByLevel.has(item.level)) {
      headingsByLevel.set(item.level, [])
    }
    headingsByLevel.get(item.level)!.push(item)
  })

  let modifiedHTML = content

  headingsByLevel.forEach((items, level) => {
    const headingRegex = new RegExp(
      `(<h${level}(?:[^>]*?)(?:\\s+id=["'][^"']*["'])?\\s*>)([\\s\\S]*?)(<\\/h${level}>)`,
      'gi'
    )

    const matches: Array<{
      fullMatch: string
      openTag: string
      content: string
      closeTag: string
      index: number
    }> = []

    let match
    while ((match = headingRegex.exec(modifiedHTML)) !== null) {
      if (match[1].includes('id=')) continue
      matches.push({
        fullMatch: match[0],
        openTag: match[1],
        content: match[2],
        closeTag: match[3],
        index: match.index,
      })
    }

    const replacements: Array<{ index: number; length: number; replacement: string }> = []
    const processedMatches = new Set<number>()

    items.forEach((tocItem) => {
      for (let i = 0; i < matches.length; i++) {
        if (processedMatches.has(i)) continue
        const headingMatch = matches[i]
        const headingText = extractHeadingText(headingMatch.content)
        if (headingText.toLowerCase() === tocItem.text.toLowerCase()) {
          const newOpenTag = headingMatch.openTag.replace(/\s*>/, ` id="${tocItem.id}">`)
          const replacement = newOpenTag + headingMatch.content + headingMatch.closeTag
          replacements.push({
            index: headingMatch.index,
            length: headingMatch.fullMatch.length,
            replacement,
          })
          processedMatches.add(i)
          break
        }
      }
    })

    replacements.sort((a, b) => b.index - a.index)
    replacements.forEach(({ index, length, replacement }) => {
      modifiedHTML =
        modifiedHTML.substring(0, index) + replacement + modifiedHTML.substring(index + length)
    })
  })

  return modifiedHTML
}

export function useTableOfContents({
  content,
  showReadingTimes = false,
  minHeadings = 2
}: UseTOCOptions): UseTOCReturn {
  const tocItems = useMemo(() => extractHeadings(content), [content])

  const sectionReadingTimes = useMemo(() => {
    if (!showReadingTimes || tocItems.length === 0) return {}

    // 500 wpm — same constant used across the project (excerpt-generator,
    // typography-utils, directus calculateReadingTime, content-analyzers).
    const WORDS_PER_MINUTE = 500
    const times: Record<string, number> = {}

    // Locate each heading in the raw HTML so we can measure the text between
    // this heading and the next one. We search for the heading's id attribute
    // (injected by injectHeadingIds) to get an accurate byte position.
    const getHeadingPos = (id: string): number => {
      const idx = content.indexOf(`id="${id}"`)
      return idx === -1 ? content.indexOf(`id='${id}'`) : idx
    }

    const positions = tocItems.map(item => getHeadingPos(item.id))

    tocItems.forEach((item, i) => {
      const start = positions[i]
      const end = i + 1 < positions.length ? positions[i + 1] : content.length
      const slice = start === -1 ? '' : content.slice(start, end)
      // Strip tags; split on whitespace for word count.
      const wordCount = slice.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length
      times[item.id] = Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE))
    })

    return times
  }, [content, tocItems, showReadingTimes])

  const processedContent = useMemo(
    () => injectHeadingIds(content, tocItems),
    [content, tocItems]
  )

  const shouldShowTOC = tocItems.length >= minHeadings

  return {
    tocItems,
    shouldShowTOC,
    sectionReadingTimes,
    processedContent,
  }
}
